import { enqueueSnackbar } from '~/utils/notifications';
import { useEffect } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import {
    ContourRedoRequestedEvent,
    ContourUndoRequestedEvent,
    domainEventBus,
    RoiPropertiesUpdatedEvent,
    StructureSetApprovalChangeRequestedEvent,
    StructureSetContourThicknessChangeRequestedEvent,
    StructureSetDescriptionChangeRequestedEvent,
    StructureSetLabelChangeRequestedEvent,
    StructureSetVisibilityChangeRequestedEvent,
} from '~/domain/events';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { createStructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import {
    currentStructureSetIdState,
    currentStructureSetState,
} from '~/state/atoms/structureSet/structureSetAtoms';

/**
 * Application service layer for {@link StructureSet} operations.
 *
 * Processes structure set domain events by coordinating between domain models,
 * state management, and external systems.
 */
export function useStructureSetHandlers() {
    const currentStructureSetId = useRecoilValue(currentStructureSetIdState);
    const session = useSessionOrNull();
    const handleStructureSetApprovalChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: StructureSetApprovalChangeRequestedEvent) => {
                try {
                    if (!currentStructureSetId) {
                        console.warn(
                            'No structure set available for approval change'
                        );
                        return;
                    }

                    const { set } = recoilInterface;
                    const structureSet =
                        session?.patient.getStructureSetWithSopInstanceUid(
                            currentStructureSetId
                        );
                    if (!structureSet) {
                        console.warn(
                            `Structure set not found: ${currentStructureSetId}`
                        );
                        return;
                    }
                    session?.context.tracker.command(() => {
                        structureSet.setApprovalStatus(
                            event.newApprovalStatus,
                            event.reviewerName,
                            event.source === 'user' // Only cascade for user events
                        );
                    });
                    const updatedReadModel =
                        createStructureSetReadModel(structureSet);
                    set(currentStructureSetState, updatedReadModel);

                    // Single owner of the structure set approval
                    // notification: every set-level approval transition is
                    // an explicit user action (the dropdown → confirmation
                    // dialog), so each gets a plain confirmation.
                    if (
                        event.newApprovalStatus === DICOMApprovalStatus.APPROVED
                    ) {
                        enqueueSnackbar(
                            `Structure set approval status was changed to ${DICOMApprovalStatus.APPROVED}.`,
                            { variant: 'success' }
                        );
                    } else if (
                        event.newApprovalStatus === DICOMApprovalStatus.REJECTED
                    ) {
                        enqueueSnackbar(
                            `Structure set approval status was changed to ${DICOMApprovalStatus.REJECTED}.`,
                            {
                                variant: 'warning',
                            }
                        );
                    } else if (
                        event.newApprovalStatus ===
                        DICOMApprovalStatus.UNAPPROVED
                    ) {
                        enqueueSnackbar(
                            `Structure set approval status was changed to ${DICOMApprovalStatus.UNAPPROVED}.`,
                            { variant: 'warning' }
                        );
                    }

                    // Emit individual ROI property updated events for each ROI
                    // only when cascading occurred (user events)
                    if (event.source === 'user') {
                        const roiUpdatePromises = updatedReadModel.roiArray.map(
                            (roi) =>
                                domainEventBus.publish(
                                    new RoiPropertiesUpdatedEvent(
                                        roi.roiNumber,
                                        {
                                            approvalStatus:
                                                event.newApprovalStatus,
                                        },
                                        'system'
                                    )
                                )
                        );

                        await Promise.all(roiUpdatePromises);
                    }
                } catch (error) {
                    console.error(
                        `Failed to handle structure set approval change: ${error}`
                    );
                }
            },
        [currentStructureSetId, session]
    );

    const handleStructureSetLabelChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: StructureSetLabelChangeRequestedEvent) => {
                try {
                    if (!currentStructureSetId) {
                        console.warn(
                            'No structure set available for label change'
                        );
                        return;
                    }
                    const { set } = recoilInterface;
                    const structureSet =
                        session?.patient.getStructureSetWithSopInstanceUid(
                            currentStructureSetId
                        );
                    if (!structureSet) {
                        console.warn(
                            `Structure set not found: ${currentStructureSetId}`
                        );
                        return;
                    }
                    session?.context.tracker.command(() => {
                        structureSet.rename(event.newLabel);
                    });
                    set(
                        currentStructureSetState,
                        createStructureSetReadModel(structureSet)
                    );
                } catch (error) {
                    console.error(
                        'Failed to update structure set label:',
                        error
                    );
                    throw error;
                }
            },
        [currentStructureSetId, session]
    );

    const handleStructureSetDescriptionChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: StructureSetDescriptionChangeRequestedEvent) => {
                try {
                    if (!currentStructureSetId) {
                        console.warn(
                            'No structure set available for description change'
                        );
                        return;
                    }

                    const { set } = recoilInterface;
                    const structureSet =
                        session?.patient.getStructureSetWithSopInstanceUid(
                            currentStructureSetId
                        );
                    if (!structureSet) {
                        console.warn(
                            `Structure set not found: ${currentStructureSetId}`
                        );
                        return;
                    }
                    session?.context.tracker.command(() => {
                        structureSet.setDescription(event.newDescription);
                    });
                    set(
                        currentStructureSetState,
                        createStructureSetReadModel(structureSet)
                    );
                } catch (error) {
                    console.error(
                        'Failed to update structure set description:',
                        error
                    );
                    throw error;
                }
            },
        [currentStructureSetId, session]
    );

    const handleStructureSetVisibilityChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: StructureSetVisibilityChangeRequestedEvent) => {
                try {
                    if (!currentStructureSetId) {
                        console.warn(
                            'No structure set available for visibility change'
                        );
                        return;
                    }

                    const { set } = recoilInterface;
                    const structureSet =
                        session?.patient.getStructureSetWithSopInstanceUid(
                            currentStructureSetId
                        );
                    if (!structureSet) {
                        console.warn(
                            `Structure set not found: ${currentStructureSetId}`
                        );
                        return;
                    }
                    // Tracked seam: emits one coalesced changeset so the
                    // version bumps and version-keyed projections refresh.
                    // The per-ROI engine events below still drive the
                    // viewport (EngineAdapter.onRoiModified does not yet
                    // forward visibility — that consolidation is deferred).
                    session?.structureSetService.setVisibility(
                        { id: structureSet.id },
                        event.visible
                    );
                    const updatedReadModel =
                        createStructureSetReadModel(structureSet);
                    set(currentStructureSetState, updatedReadModel);

                    // Emit individual ROI property updated events for each ROI
                    const roiUpdatePromises = updatedReadModel.roiArray.map(
                        (roi) =>
                            domainEventBus.publish(
                                new RoiPropertiesUpdatedEvent(
                                    roi.roiNumber,
                                    { visible: event.visible },
                                    event.source
                                )
                            )
                    );

                    await Promise.all(roiUpdatePromises);
                } catch (error) {
                    console.error(
                        `Failed to handle structure set visibility change: ${error}`
                    );
                }
            },
        [currentStructureSetId, session]
    );

    const handleStructureSetContourThicknessChangeRequest = useRecoilCallback(
        (recoilInterface) =>
            async (event: StructureSetContourThicknessChangeRequestedEvent) => {
                try {
                    if (!currentStructureSetId) {
                        console.warn(
                            'No structure set available for contour thickness change'
                        );
                        return;
                    }

                    const { set } = recoilInterface;
                    const structureSet =
                        session?.patient.getStructureSetWithSopInstanceUid(
                            currentStructureSetId
                        );
                    if (!structureSet) {
                        console.warn(
                            `Structure set not found: ${currentStructureSetId}`
                        );
                        return;
                    }
                    // Tracked seam (see the visibility handler): one
                    // coalesced changeset + version bump; the per-ROI engine
                    // events below still drive the viewport.
                    session?.structureSetService.setContourThickness(
                        { id: structureSet.id },
                        event.contourThickness
                    );
                    const updatedReadModel =
                        createStructureSetReadModel(structureSet);
                    set(currentStructureSetState, updatedReadModel);

                    // Emit individual ROI property updated events for each ROI
                    const roiUpdatePromises = updatedReadModel.roiArray.map(
                        (roi) =>
                            domainEventBus.publish(
                                new RoiPropertiesUpdatedEvent(
                                    roi.roiNumber,
                                    {
                                        contourThickness:
                                            event.contourThickness,
                                    },
                                    event.source
                                )
                            )
                    );

                    await Promise.all(roiUpdatePromises);
                } catch (error) {
                    console.error(
                        `Failed to handle structure set contour thickness change: ${error}`
                    );
                }
            },
        [currentStructureSetId, session]
    );

    const handleStructureSetUndoOrRedoRequest = useRecoilCallback(
        (recoilInterface) =>
            (event: ContourUndoRequestedEvent | ContourRedoRequestedEvent) => {
                try {
                    if (!currentStructureSetId) {
                        console.warn('No structure set open');
                        return;
                    }

                    const { set } = recoilInterface;
                    const structureSet =
                        session?.patient.getStructureSetWithSopInstanceUid(
                            currentStructureSetId
                        );
                    if (!structureSet) {
                        console.warn(
                            `Structure set not found: ${currentStructureSetId}`
                        );
                        return;
                    }
                    if (!structureSet.getRoiByNumber(event.roiNumber)) {
                        console.warn(
                            `ROI ${event.roiNumber} not found in structure set ${currentStructureSetId}`
                        );
                        return;
                    }
                    // Undo/redo mutations happen in the engine; rebuild the
                    // read model so React state reflects the new state.
                    set(
                        currentStructureSetState,
                        createStructureSetReadModel(structureSet)
                    );
                } catch (error) {
                    console.error(
                        `Failed to handle structure set visibility change: ${error}`
                    );
                }
            },
        [currentStructureSetId, session]
    );

    useEffect(() => {
        const subscriptions = [
            domainEventBus.subscribe(
                StructureSetApprovalChangeRequestedEvent.EVENT_TYPE,
                handleStructureSetApprovalChangeRequest
            ),
            domainEventBus.subscribe(
                StructureSetLabelChangeRequestedEvent.EVENT_TYPE,
                handleStructureSetLabelChangeRequest
            ),
            domainEventBus.subscribe(
                StructureSetDescriptionChangeRequestedEvent.EVENT_TYPE,
                handleStructureSetDescriptionChangeRequest
            ),
            domainEventBus.subscribe(
                StructureSetVisibilityChangeRequestedEvent.EVENT_TYPE,
                handleStructureSetVisibilityChangeRequest
            ),
            domainEventBus.subscribe(
                StructureSetContourThicknessChangeRequestedEvent.EVENT_TYPE,
                handleStructureSetContourThicknessChangeRequest
            ),
            domainEventBus.subscribe(
                ContourRedoRequestedEvent.EVENT_TYPE,
                handleStructureSetUndoOrRedoRequest
            ),
            domainEventBus.subscribe(
                ContourUndoRequestedEvent.EVENT_TYPE,
                handleStructureSetUndoOrRedoRequest
            ),
        ];

        return () => {
            subscriptions.forEach((sub) => sub.unsubscribe());
        };
    }, [
        handleStructureSetApprovalChangeRequest,
        handleStructureSetLabelChangeRequest,
        handleStructureSetDescriptionChangeRequest,
        handleStructureSetVisibilityChangeRequest,
        handleStructureSetContourThicknessChangeRequest,
        handleStructureSetUndoOrRedoRequest,
    ]);
}
